import { type IReactionDisposer, makeAutoObservable } from 'mobx'
import { type CancelTokenSource } from 'axios'
import { nanoid } from 'nanoid'
import { Icon, Typography } from 'shared/ui'
import { LINK_COMPLIANCE_CALLING_LEARN_MORE_LINK } from 'shared/constants/links'
import { uiStore } from 'shared/store/uiStore'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { FeatureRestrictedModalStore } from 'entities/UsersPermissions'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { BillingPath } from 'entities/ExportRouters'
import { type CompliancePageStore } from 'pages/settings/pages/compliance/store/compliancePageStore'
import type { IRegisterSectionProps } from 'pages/settings/pages/compliance/ui/RegisterSection/RegisterSection'
import styles from 'pages/settings/pages/compliance/ui/styles.module.scss'
import { CallingRoutesSteps } from 'pages/settings/pages/compliance/pages/calling/route/type'
import { SettingsPath } from 'pages/settings/route/settingsPath'
import { EditCnamNumbersStore } from 'pages/settings/pages/compliance/pages/calling/pages/EditCnamNumbers/store/EditCnamNumbersStore'
import { NumbersPath } from 'pages/settings/pages/numbers/route/numbersPath'
import { AddOnModalStore } from 'pages/settings/ui/AddOnModal/store/addOnModalStore'
import { SummaryStore } from './SummaryStore'

export class CallingPageStore {
  private _disposePageReaction: IReactionDisposer | null = null
  private _cancelPageSource: CancelTokenSource | null = null

  private readonly _addOnModalStore: AddOnModalStore
  private readonly _summaryStore: SummaryStore
  private readonly _editCnamNumbersStore: EditCnamNumbersStore

  private _modalId = nanoid()

  constructor(
    private _compliancePageStore: CompliancePageStore,
    private _entities: IEntitiesStore
  ) {
    makeAutoObservable(this)

    this._addOnModalStore = new AddOnModalStore(this._entities, 2, () =>
      this.complianceLayerStore.preventSubmitModal()
    )
    this._summaryStore = new SummaryStore(this, this._entities)
    this._editCnamNumbersStore = new EditCnamNumbersStore(this._entities, this)
    this.init()
  }

  get addOnModalStore() {
    return this._addOnModalStore
  }

  get summaryStore() {
    return this._summaryStore
  }

  get compliancePageStore() {
    return this._compliancePageStore
  }

  get editCnamNumbersStore() {
    return this._editCnamNumbersStore
  }

  get billingComplianceInfo() {
    return this.compliancePageStore.complianceLayerStore.billingComplianceInfo
  }

  get summary() {
    return this.compliancePageStore.complianceLayerStore.summary
  }

  get complianceLayerStore() {
    return this.compliancePageStore.complianceLayerStore
  }

  get businessProfile() {
    return this.complianceLayerStore.businessProfile
  }

  get allStepsLength() {
    return 2
  }

  get disabledDefault() {
    return !this.isGetAddOn
  }

  get disabledAddOn() {
    // Only  for 1.0 version
    if (this.businessProfile?.isStatusEmpty) return false

    return !this.businessProfile?.isStatusApproved
  }

  get isGetAddOn() {
    return Boolean(this.billingComplianceInfo?.subscribed)
  }

  get voiceIntegrityActionButton() {
    const { status } = this._entities.complianceStore.organizationVoiceIntegrity || {}

    if (!status) {
      return {
        text: 'Register',
        disabled: this.disabledDefault,
        onClick: () => {
          if (this._entities.featureFlagsStore.salesmsg_design_v2_compliance_calling) {
            uiStore.changeRoute({
              path: `/${SettingsPath.compliance.calling.root}/${CallingRoutesSteps.enableVoiceIntegrity}`,
            })
          } else {
            window.publicApiVue?.showVoiceIntegrityModal()
          }
        },
      }
    }
    if (status === 'failed') {
      return {
        text: 'Update',
        onClick: () => {
          if (this._entities.featureFlagsStore.salesmsg_design_v2_compliance_calling) {
            uiStore.changeRoute({
              path: `/${SettingsPath.compliance.calling.root}/${CallingRoutesSteps.enableVoiceIntegrity}`,
            })
          }
        },
      }
    }

    return undefined
  }

  get callingVoiceIntegrityRegisterSectionProps(): IRegisterSectionProps {
    const status = this._entities.complianceStore.organizationVoiceIntegrity?.status

    const sectionType: IRegisterSectionProps['section'] = status
      ? 'border'
      : this.isGetAddOn
      ? 'default'
      : 'gray'

    return {
      section: sectionType,
      title: 'Voice Integrity: Reputation Monitoring',
      desc: 'Remediate “Spam Likely” labels on your calls.',
      actionIcon: Boolean(status)
        ? {
            onClick: () => {
              if (this._entities.featureFlagsStore.salesmsg_design_v2_compliance_calling) {
                this.summaryStore.openVoiceIntegritySummaryModal()
              } else {
                window.publicApiVue?.showShakenStirDeleteModal()
              }
            },
          }
        : undefined,
      actionButton: this.voiceIntegrityActionButton,
      statusProps: this.compliancePageStore.getIconCondition({
        status: status,
      }),
    }
  }

  get callingStirShakenRegisterSectionProps(): IRegisterSectionProps {
    const status = this.shakenStir?.status
    const sectionType: IRegisterSectionProps['section'] = status
      ? 'border'
      : this.isGetAddOn
      ? 'default'
      : 'gray'

    return {
      section: sectionType,
      title: 'STIR / SHAKEN',
      desc: 'Increase answer rates by showing “Caller Verified”.',
      actionButton: !Boolean(status)
        ? {
            text: 'Register',
            disabled: this.disabledDefault,
            onClick: () => {
              if (this._entities.featureFlagsStore.salesmsg_design_v2_compliance_calling) {
                uiStore.changeRoute({
                  path: `/${SettingsPath.compliance.calling.root}/${CallingRoutesSteps.enableStirShaken}`,
                })
              } else {
                window.publicApiVue?.showShakenStirAdvanced()
              }
            },
          }
        : undefined,
      statusProps: this.compliancePageStore.getIconCondition({
        status: status,
      }),
    }
  }

  get callingCnamCallerIdRegisterSectionProps(): IRegisterSectionProps {
    const hasCnam = Boolean(this.organizationCnam?.hasCnam)

    const sectionType: IRegisterSectionProps['section'] = hasCnam
      ? 'border'
      : this.isGetAddOn
      ? 'default'
      : 'gray'

    return {
      section: sectionType,
      title: 'CNAM Caller ID',
      desc: "Brand your outgoing calls with your company's name.",
      actionButton: {
        text: hasCnam ? 'Add' : 'Register',
        icon: hasCnam ? 'add' : undefined,
        contained: hasCnam ? 'secondary' : undefined,
        disabled: this.disabledDefault,
        onClick: this.callerIdRegisterClick,
      },
      items: this.organizationCnam?.cnam.length
        ? this.organizationCnam?.cnam.map((item) => {
            return {
              id: item.id,
              title: item.name,
              desc: `${item.count} ${item.count === 1 ? 'number' : 'numbers'}`,
              statusProps: this.compliancePageStore.getIconCondition({
                status: item.status,
              }),
              actionIcon: {
                onClick: (id) => {
                  const item = this.organizationCnam?.getItem(id)
                  if (this._entities.featureFlagsStore.salesmsg_design_v2_compliance_calling) {
                    item && this.summaryStore.openCnamCallerIdSummaryModal(item)
                  } else {
                    item && window.publicApiVue?.showCNAMCallerDelete(item.origin)
                  }
                },
              },
              actionButton:
                item.status === 'failed'
                  ? {
                      text: 'Update',
                      onClick: () => {
                        uiStore.changeRoute({
                          path: `/${SettingsPath.compliance.calling.root}/${CallingRoutesSteps.enableCnamCallerId}`,
                        })
                        this.summaryStore.openCnamToUpdate(item)
                      },
                    }
                  : undefined,
            }
          })
        : [],
    }
  }

  get callingHeaderSectionProps(): IRegisterSectionProps {
    return {
      section: this.isGetAddOn ? 'default' : 'gray',
      title: (
        <Typography ariaLabel='Trusted_Calling_title' tag='div' className={styles.sectionTitle}>
          <Typography
            ariaLabel='trustedCalling'
            className={styles.sectionTitleText}
            variant='body-lg-medium'
          >
            Trusted Calling
          </Typography>
          <Icon className={styles.flag} icon='flagUSA' />
          <Icon className={styles.flag} icon='flagCanada' />
        </Typography>
      ),
      desc: (
        <Typography ariaLabel='calling_verification_desc' variant='body-md-regular' tag='div'>
          Improves answer rates, brand recognition, and trust.
        </Typography>
      ),
      learnMoreLink: LINK_COMPLIANCE_CALLING_LEARN_MORE_LINK,
      actionButton: !this.isGetAddOn
        ? {
            text: 'Get add-on',
            onClick: () => {
              if (
                this._entities.usersStore.user?.isAdminRole &&
                !this._entities.userPermissionsStore.getItem('can_manage_billing_settings')
              ) {
                new FeatureRestrictedModalStore('can_manage_billing_settings')

                return
              }
              if (this._entities.subscriptionStore.isTrial) {
                uiStore.changeRouteViaVue({
                  path: `/${BillingPath.upgrade}`,
                })

                return
              }

              if (this.businessProfile?.isReviewStatus) {
                this.openBusinessProfileInReviewModal()

                return
              }

              if (this.businessProfile?.isNotReady) {
                this.showBusinessProfileRegisterModal()

                return
              }

              this._entities.featureFlagsStore.salesmsg_design_v2_compliance_calling
                ? this.clickGetAddOn()
                : this.clickGetAddOnVue()
            },
          }
        : undefined,
    }
  }

  get voiceIntegrity() {
    return this.complianceLayerStore.voiceIntegrity
  }

  get shakenStir() {
    return this.complianceLayerStore.shakenStir
  }

  get organizationCnam() {
    return this.complianceLayerStore.organizationCnam
  }

  clickGetAddOn = async () => {
    if (this._entities.subscriptionStore.isPAYGPlan) {
      uiStore.changeRouteViaVue({
        path: `/${BillingPath.upgrade}`,
      })

      return
    }

    await this.addOnModalStore.openGetAddOnModal()
  }

  init = async () => {
    if (!this.isGetAddOn) {
      await this._addOnModalStore.priceInfoStore.loadPriceData()
    }
  }

  clickGetAddOnVue = () => {
    if (this._entities.subscriptionStore.isPAYGPlan) {
      uiStore.changeRouteViaVue({
        path: `/${BillingPath.upgrade}`,
      })
      return
    }
    if (this.disabledAddOn) return
    window.publicApiVue?.showTrustedCallingBuy('trust_calling')
  }

  openBusinessProfileInReviewModal = () => {
    modalStore.addModal({
      id: this._modalId,
      type: ModalTypeList.CLOCK,
      showCloseButton: false,
      title: 'Business profile is in carrier review',
      desc: "Your business profile is being reviewed by carriers. Once approved, you'll be able to activate the Trusted Calling add-on.",
      primaryAction: {
        text: 'Done',
        onAction: () => modalStore.closeModal(this._modalId),
      },
    })
  }

  openBuyNumberModal = () => {
    modalStore.addModal({
      id: this._modalId,
      type: ModalTypeList.INFO,
      title: 'Buy phone number',
      desc: 'To add Caller ID, you first need to buy a phone number.',
      primaryAction: {
        text: 'Buy number',
        onAction: () => {
          modalStore.closeModal(this._modalId)

          uiStore.changeRoute({
            path: `${NumbersPath.buyNumber.stepsRoot}`,
          })
        },
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => modalStore.removeModal(this._modalId),
      },
    })
  }

  callerIdRegisterClick = () => {
    if (!this._entities.featureFlagsStore.salesmsg_design_v2_compliance_calling) {
      window.publicApiVue?.showCNAMCallerID()

      return
    }

    this._entities.complianceStore.complianceVoiceServicesStore.freeNumbers.length
      ? uiStore.changeRoute({
          path: `/${SettingsPath.compliance.calling.root}/${CallingRoutesSteps.enableCnamCallerId}`,
        })
      : this.openBuyNumberModal()
  }

  dispose = () => {
    this._disposePageReaction?.()
    this._cancelPageSource?.cancel()
  }

  showBusinessProfileRegisterModal = () => {
    this.compliancePageStore.businessProfilePageStore.openBusinessProfileRegisterModal('calling')
  }

  closeSetting = () => {
    uiStore.changeRoute({
      path: `/${SettingsPath.compliance.localNumbers.root}`,
    })
  }
}
